import type { LucideProps } from "lucide-react";
import React, { forwardRef } from "react";
import type { SVGProps } from "react";

import { iconMap, type IconName } from "../../constants";
import { customIconMap, type CustomIconName } from "../../custom-icons";
import { getIconSize, type IconSize } from "../../utils";

// ---------------------------------------------------------------------------
// Prop types — discriminated union enforces the correct name per icon set
// ---------------------------------------------------------------------------

type BaseProps = {
  size?: IconSize | number;
  "aria-label"?: string;
};

type LucideIconProps = BaseProps &
  Omit<LucideProps, "size" | "ref" | "name"> & {
    iconSet?: "lucide";
    name: IconName;
  };

type CustomIconProps = BaseProps &
  Omit<SVGProps<SVGSVGElement>, "name" | "ref"> & {
    iconSet: "custom";
    name: CustomIconName;
  };

export type IconProps = LucideIconProps | CustomIconProps;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Icon = forwardRef<SVGSVGElement, IconProps>(
  (
    {
      name,
      size = "md",
      "aria-label": ariaLabel,
      iconSet = "lucide",
      ...props
    },
    ref
  ) => {
    const iconSize = getIconSize(size);
    const isDecorative = !ariaLabel;

    const sharedA11yProps = {
      "aria-hidden": isDecorative ? ("true" as const) : undefined,
      "aria-label": ariaLabel,
      role: isDecorative ? undefined : "img",
    };

    if (iconSet === "custom") {
      const CustomIcon = customIconMap[name as CustomIconName];

      if (!CustomIcon) {
        console.warn(`Icon "${name}" not found in custom icon map`);
        return null;
      }

      // Extract strokeWidth so we can provide the custom-icon-specific default (1.5)
      // without affecting Lucide's default (2).
      const { strokeWidth, ...svgProps } = props as SVGProps<SVGSVGElement> & {
        strokeWidth?: number;
      };

      return (
        <CustomIcon
          ref={ref}
          width={iconSize}
          height={iconSize}
          strokeWidth={strokeWidth ?? 1.5}
          {...sharedA11yProps}
          {...svgProps}
        />
      );
    }

    const LucideIcon = iconMap[name as IconName];

    if (!LucideIcon) {
      console.warn(`Icon "${name}" not found in icon map`);
      return null;
    }

    return (
      <LucideIcon
        ref={ref}
        size={iconSize}
        focusable="false"
        {...sharedA11yProps}
        {...props}
      />
    );
  }
);

Icon.displayName = "Icon";

export { Icon };
